import { useEffect, useMemo, VFC } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { ExternalIntegrationSlackOAuthAttributes } from 'dashboard/models/external-integration';
import { WorkflowActionMeta, WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionSlackOAuthSendMessageV1 } from 'dashboard/models/workflow-version';
import { RootState } from 'dashboard/redux/store';
import { loadExternalIntegration } from 'dashboard/redux/thunk-actions/external-integrations';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { JsonSchemaStringifiableTypes } from 'lib/fields';
import useApiCallback from 'lib/hooks/useApiCallback';
import { ApiModel } from 'lib/interfaces/api';

const RECIPIENT_TYPE_OPTIONS = [
  { label: 'User', value: 'user', listType: 'users-list' },
  {
    label: 'Channel',
    value: 'channel',
    listType: 'channels-list',
  },
];

interface ParamsSlackOAuthSendMessageV1Props extends PropsFromRedux {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionSlackOAuthSendMessageV1['params'];
  usableObjects: UsableObjectConfig[];
  metaActionIntegrations: WorkflowActionMeta['external-integrations'];
  updateActionParams: (newParams: WorkflowActionSlackOAuthSendMessageV1['params']) => void;
}

const ParamsSlackOAuthSendMessageV1: VFC<ParamsSlackOAuthSendMessageV1Props> = ({
  editable,
  errors,
  params,
  usableObjects,
  updateActionParams,
  metaActionIntegrations,
  loadExternalIntegration,
  externalIntegrations,
}) => {
  const updateSendSlackMessageParams = <
    Key extends keyof WorkflowActionSlackOAuthSendMessageV1['params'],
  >(
    key: Key,
    value: WorkflowActionSlackOAuthSendMessageV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const { callback: loadExternalIntegrationDetails, loading: isLoading } = useApiCallback(
    async (integrationId: string, recipientType) => {
      await loadExternalIntegration(
        integrationId,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        RECIPIENT_TYPE_OPTIONS.find((i) => i.value === recipientType).listType,
      );
    },
    [],
  );

  useEffect(() => {
    if (params['external-integration-id'] && params['recipient-type']) {
      const { integrationAttributes, listType } = findRequestedListInRedux();
      !integrationAttributes?.[listType as keyof ExternalIntegrationSlackOAuthAttributes]
        ? loadExternalIntegrationDetails(
            params['external-integration-id'],
            params['recipient-type'],
          )
        : null;
    }
  }, [params['external-integration-id'], params['recipient-type'], loadExternalIntegrationDetails]);

  const processedIntegrations = metaActionIntegrations
    .filter((i) => i.type === ApiModel.ExternalIntegrationProductivitySlackOAuth)
    .map((int) => ({
      label: int.name,
      value: int.token,
    }));

  const connectionOptions = [
    ...processedIntegrations,
    { label: 'Add New Slack Workspace', value: 'marketplace-managment-redirect' },
  ];

  const navigate = useNavigate();
  const handleConnectionChange = (value: string) => {
    if (value === 'marketplace-managment-redirect') {
      navigate(`/dashboard/marketplace/slack`);
    } else {
      updateSendSlackMessageParams('external-integration-id', value);
    }
  };

  const findRequestedListInRedux = () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const integrationAttributes: ExternalIntegrationSlackOAuthAttributes =
      externalIntegrations.find((i) => i.id === params['external-integration-id'])?.attributes;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const listType = RECIPIENT_TYPE_OPTIONS.find(
      (i) => i.value === params['recipient-type'],
    ).listType;

    return { integrationAttributes, listType };
  };

  const recipientsOptionsList = useMemo(() => {
    const { integrationAttributes, listType } = findRequestedListInRedux();
    const transformRecipientsListToOptions = (
      attributes: ExternalIntegrationSlackOAuthAttributes,
    ) => {
      return Object.entries(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        attributes[listType as keyof ExternalIntegrationSlackOAuthAttributes],
      ).map(([value, label]) => ({
        value,
        label,
      }));
    };

    if (integrationAttributes && listType in integrationAttributes) {
      return transformRecipientsListToOptions(integrationAttributes);
    } else {
      return [];
    }
  }, [externalIntegrations, params['recipient-type']]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionSlackOAuthSendMessageV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <FormControlLabel htmlFor='params-slack-send-message__external-integration-id'>
        Slack workspace
      </FormControlLabel>
      <Select
        value={params['external-integration-id']}
        onValueChange={handleConnectionChange}
        id='params-slack-send-message__external-integration-id'
        disabled={!editable || isLoading}
        hasError={hasErrorFor('external-integration-id')}
        placeholder='Select Slack Connection'
      >
        {connectionOptions.map((opt) => (
          <Option key={opt.label} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('external-integration-id')}
      <Spacer height={2} />
      <FormControlLabel htmlFor='params-slack-send-message__recipient-type'>
        Recipient type
      </FormControlLabel>
      <Select
        value={params['recipient-type']}
        onValueChange={(value: string) => {
          updateSendSlackMessageParams('recipient-type', value);
        }}
        id='params-slack-send-message__recipient-type'
        disabled={!editable || isLoading}
        hasError={hasErrorFor('external-integration-id')}
      >
        {RECIPIENT_TYPE_OPTIONS.map((opt) => (
          <Option key={opt.label} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('recipient-type')}
      <Spacer height={2} />
      <FormControlLabel htmlFor='params-slack-send-message__recipient-id'>
        Recipient ID
      </FormControlLabel>
      <Select
        value={params['recipient-id']}
        onValueChange={(value: string) => {
          updateSendSlackMessageParams('recipient-id', value);
        }}
        id='params-slack-send-message__recipient-id'
        disabled={!editable || isLoading}
        hasError={hasErrorFor('recipient-id')}
        placeholder='Choose Recipients'
      >
        {recipientsOptionsList.map((opt) => (
          <Option key={opt.label} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('recipient-id')}
      <Spacer height={2} />
      <FormControlLabel htmlFor='params-slack-send-message__message-input'>
        Message
      </FormControlLabel>
      <HandlebarFormControl
        type='text-area'
        value={params['message']}
        updateValue={(value: string) => {
          updateSendSlackMessageParams('message', value);
        }}
        editable={editable && !isLoading}
        allowedTypes={JsonSchemaStringifiableTypes}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-slack-send-message__message-input'
        hasError={hasErrorFor('message')}
      />
      {renderErrorMessageFor('message')}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    externalIntegrations: state.externalIntegrations.externalIntegrations,
  };
};

const mapDispatchToProps = {
  loadExternalIntegration,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ParamsSlackOAuthSendMessageV1);
